import { FEEDBACK_MESSAGE_MAX_LENGTH } from '@iculogbook/shared';
import { useState } from 'react';

import { errorCopy } from '../../auth/errorCopy.ts';
import { Button, Dialog, Field, NO_IDENTIFIABLE_INFORMATION, TextArea, useToast } from '../../components/index.ts';

/**
 * Settings → About → Send feedback. One message, emailed to the operator, not
 * stored anywhere in this app — see COMPLIANCE.md. If a reply is wanted, it
 * happens by email, outside ICULogbook.
 */
export function FeedbackDialog() {
  const { showToast } = useToast();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  function close(): void {
    setOpen(false);
    setMessage('');
  }

  async function send(): Promise<void> {
    setBusy(true);
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: message.trim() }),
      });

      if (response.ok) {
        close();
        showToast({ message: 'Sent. Thank you.', tone: 'success' });
        return;
      }

      const body = (await response.json().catch(() => null)) as { error?: { code?: string } } | null;

      showToast({ message: errorCopy(body?.error?.code ?? null), tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="text-accent underline hover:text-accent-hover"
        onClick={() => setOpen(true)}
      >
        Send feedback
      </button>

      <Dialog
        open={open}
        onClose={close}
        title="Send feedback"
        description="Goes straight to the person building ICULogbook, by email. Not a support ticket — for anything urgent about your own account, use the contact address on the Privacy notice."
        footer={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={message.trim() === ''}
              onClick={() => void send()}
            >
              Send
            </Button>
          </>
        }
      >
        <Field label="Message" help={NO_IDENTIFIABLE_INFORMATION} className="pt-2">
          <TextArea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={FEEDBACK_MESSAGE_MAX_LENGTH}
            rows={5}
            autoFocus
          />
        </Field>
      </Dialog>
    </>
  );
}
